import { Component, OnInit } from '@angular/core';
import { HttpErrorResponse } from '@angular/common/http';
import { forkJoin } from 'rxjs';
import {
  LoginRiskDeploymentStatusDto,
  MlAutomaticRollbackStatusDto,
  MlCandidateGovernanceDto,
  MlModelDeploymentDto,
  MlNetDeploymentReadinessDto
} from '../../../_core/_model/machine-learning';
import { AuthService } from '../../../_core/_services/auth.service';
import { MachineLearningAdminService } from '../../../_core/_services/machine-learning-admin.service';
import { NotificationService } from '../../../_core/_services/notification.service';

@Component({
  selector: 'app-admin-ml-deployment-controls',
  standalone: false,
  templateUrl: './admin-ml-deployment-controls.html',
  styleUrl: './admin-ml-deployment-controls.css'
})
export class AdminMlDeploymentControls implements OnInit {
  loading = false;
  actionLoading = false;
  error = '';
  isSuperAdmin = false;
  status: LoginRiskDeploymentStatusDto | null = null;
  history: MlModelDeploymentDto[] = [];
  rollbackStatus: MlAutomaticRollbackStatusDto | null = null;
  promotionCandidates: MlCandidateGovernanceDto[] = [];
  readiness: MlNetDeploymentReadinessDto | null = null;
  selectedVersion = '';
  canaryPercentage = 10;
  notes = '';
  rollbackReason = '';
  newCanaryPercentage = 10;

  constructor(
    private mlService: MachineLearningAdminService,
    private auth: AuthService,
    private notify: NotificationService
  ) {}

  ngOnInit(): void {
    this.auth.getCurrentUser().subscribe(user => this.isSuperAdmin = user?.role === 'SuperAdmin');
    this.load();
  }

  load(): void {
    this.loading = true;
    this.error = '';
    forkJoin({
      status: this.mlService.getLoginRiskDeploymentStatus(),
      history: this.mlService.getLoginRiskDeploymentHistory(),
      rollback: this.mlService.getLoginRiskAutomaticRollbackStatus(),
      candidates: this.mlService.getLoginRiskCandidates('PromotionReady', 50)
    }).subscribe({
      next: result => {
        this.status = result.status.data;
        this.history = result.history.data || [];
        this.rollbackStatus = result.rollback.data;
        this.promotionCandidates = result.candidates.data || [];
        this.newCanaryPercentage = this.status.currentDeployment?.canaryPercentage ?? 10;
        if (!this.selectedVersion && this.promotionCandidates.length) {
          this.selectedVersion = this.promotionCandidates[0].modelVersion;
          this.checkReadiness();
        }
        this.loading = false;
      },
      error: (error: HttpErrorResponse) => {
        this.error = this.errorMessage(error, 'Unable to load deployment controls.');
        this.loading = false;
      }
    });
  }

  checkReadiness(): void {
    this.readiness = null;
    if (!this.selectedVersion) return;
    this.actionLoading = true;
    this.mlService.getLoginRiskDeploymentReadiness(this.selectedVersion).subscribe({
      next: response => this.readiness = response.data,
      error: error => this.notify.showError(this.errorMessage(error, 'Unable to evaluate deployment readiness.'), 'Error'),
      complete: () => this.actionLoading = false
    });
  }

  promote(): void {
    if (!this.isSuperAdmin || !this.readiness?.ready) return;
    this.actionLoading = true;
    this.mlService.promoteLoginRiskModel({
      modelVersion: this.selectedVersion,
      canaryPercentage: this.canaryPercentage,
      notes: this.notes || null
    }).subscribe({
      next: response => {
        this.notify.showSuccess(response.message || 'Model promoted to controlled canary deployment.', 'Promotion complete');
        this.notes = '';
        this.load();
      },
      error: error => this.notify.showError(this.errorMessage(error, 'Unable to promote the selected model.'), 'Error'),
      complete: () => this.actionLoading = false
    });
  }

  updateCanary(): void {
    const deployment = this.status?.currentDeployment;
    if (!this.isSuperAdmin || !deployment) return;
    this.actionLoading = true;
    this.mlService.updateLoginRiskCanary(deployment.id, {
      canaryPercentage: this.newCanaryPercentage,
      notes: this.notes || null
    }).subscribe({
      next: response => {
        this.notify.showSuccess(response.message || 'Canary percentage updated.', 'Success');
        this.notes = '';
        this.load();
      },
      error: error => this.notify.showError(this.errorMessage(error, 'Unable to update the canary percentage.'), 'Error'),
      complete: () => this.actionLoading = false
    });
  }

  rollback(): void {
    if (!this.isSuperAdmin || !this.rollbackReason.trim()) {
      this.notify.showWarning('A rollback reason is required.', 'Reason required');
      return;
    }
    this.actionLoading = true;
    this.mlService.rollbackLoginRiskModel(this.rollbackReason).subscribe({
      next: response => {
        this.notify.showSuccess(response.message || 'Deployment rolled back to deterministic scoring.', 'Rollback complete');
        this.rollbackReason = '';
        this.load();
      },
      error: error => this.notify.showError(this.errorMessage(error, 'Unable to roll back the deployment.'), 'Error'),
      complete: () => this.actionLoading = false
    });
  }

  evaluateAutomaticRollback(): void {
    if (!this.isSuperAdmin) return;
    this.actionLoading = true;
    this.mlService.evaluateLoginRiskAutomaticRollback().subscribe({
      next: response => {
        const result = response.data;
        this.notify.showInfo(result.message || result.action, result.rolledBack ? 'Automatic rollback executed' : 'Evaluation complete');
        this.load();
      },
      error: error => this.notify.showError(this.errorMessage(error, 'Unable to evaluate automatic rollback.'), 'Error'),
      complete: () => this.actionLoading = false
    });
  }

  badgeClass(value?: string | null): string {
    switch ((value || '').toLowerCase()) {
      case 'active': case 'healthy': case 'ready': return 'text-bg-success';
      case 'canary': case 'watch': return 'text-bg-warning text-dark';
      case 'critical': case 'rolledback': case 'superseded': return 'text-bg-danger';
      case 'ended': return 'text-bg-secondary';
      default: return 'text-bg-primary';
    }
  }

  private errorMessage(error: any, fallback: string): string {
    return error?.error?.error?.details || error?.error?.message || error?.message || fallback;
  }
}
